import { AccountTab, validTabs } from "./AccountPage.types";
import {
  IndeterminateProgress,
  ResponsiveContent,
  Tabs,
  TabsContent,
} from "@facade/components/ui";
import { Suspense, lazy, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AccountSideMenu } from "./components";
import { organizationsQueries } from "@facade/features/organization";
import { notificationPreferencesQueries } from "@facade/features/notifications";
import { usePageTitle } from "@facade/hooks";
import { useQueryClient } from "@tanstack/react-query";

const loadProfileView = () =>
  import("./views/ProfileView").then((module) => ({
    default: module.ProfileView,
  }));

const loadOrganizationsView = () =>
  import("./views/OrganizationsView").then((module) => ({
    default: module.OrganizationsView,
  }));

const loadNotificationsView = () =>
  import("./views/NotificationsView").then((module) => ({
    default: module.NotificationsView,
  }));

const ProfileView = lazy(loadProfileView);
const OrganizationsView = lazy(loadOrganizationsView);
const NotificationsView = lazy(loadNotificationsView);

const AccountTabContent = ({ activeTab }: { activeTab: AccountTab }) => {
  switch (activeTab) {
    case "profile":
      return <ProfileView />;

    case "organizations":
      return <OrganizationsView />;

    case "notifications":
      return <NotificationsView />;

    default:
      return null;
  }
};

const AccountPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tab } = useParams();
  usePageTitle("Account");

  const activeTab: AccountTab = validTabs.includes(tab as AccountTab)
    ? (tab as AccountTab)
    : "profile";

  useEffect(() => {
    if (!tab || !validTabs.includes(tab as AccountTab)) {
      navigate(`/account/profile`, { replace: true });
    }
  }, [tab, navigate]);

  const prefetchOrganizationsTab = () => {
    void loadOrganizationsView();

    void queryClient.prefetchQuery(organizationsQueries.list());
    void queryClient.prefetchQuery(organizationsQueries.invitations());
  };

  const prefetchNotificationsTab = () => {
    void loadNotificationsView();
    void queryClient.prefetchQuery(notificationPreferencesQueries.detail());
  };

  return (
    <ResponsiveContent reduced>
      <Tabs
        orientation="vertical"
        value={activeTab}
        className="gap-6 w-full flex-1 overflow-hidden p-4 pt-0"
        onValueChange={(value) => {
          navigate(`/account/${value}`);
        }}
      >
        <AccountSideMenu
          onOrganizationsIntent={prefetchOrganizationsTab}
          onNotificationsIntent={prefetchNotificationsTab}
        />
        <TabsContent value={activeTab} className="flex min-h-full">
          <Suspense key={activeTab} fallback={<IndeterminateProgress />}>
            <AccountTabContent activeTab={activeTab} />
          </Suspense>
        </TabsContent>
      </Tabs>
    </ResponsiveContent>
  );
};

export default AccountPage;
